/**
 * Anomaly Service
 * API client for cost anomaly detection
 */

import axios from 'axios';
import { authHeaders as sharedAuthHeaders } from './apiHeaders';

const OPT_API_ROOT = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
const API_BASE_URL = `${OPT_API_ROOT}/api/v1`;
// Platform-anomaly admin routes are mounted at /admin, not under /api/v1 -
// same host, different root path.
const ADMIN_BASE_URL = `${OPT_API_ROOT}/admin`;
const DEFAULT_TIMEOUT = 180000; // 3 minutes

// optimization-api now requires a verified Bearer token on every request
// (previously had no auth at all - a real gap fixed alongside this).
// x-tenant-id lets anomalies differ per company, same as the rest of the app.
// Delegates to the shared builder so a user Refresh (withFreshData) adds
// Cache-Control: no-cache here too. Ten local copies of this function is why
// the Refresh button worked on some pages and silently did nothing on others.
function authHeaders(): Record<string, string> {
  return sharedAuthHeaders();
}

export interface Anomaly {
  id: string;
  type: 'cost_spike' | 'cost_drift' | 'usage_cost_mismatch' | 'pattern_anomaly' | 'new_service' | 'unusual_usage';
  service: string;
  provider: 'aws' | 'azure' | 'gcp';
  current_cost: number;
  normal_cost: number;
  increase_percent: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  detected_at: string;
  message: string;
  status: 'open' | 'acknowledged' | 'resolved' | 'false_positive';
  metadata?: Record<string, any>;
  delta_amount?: number | null;
  delta_percent?: number | null;
  /** FR-ANOM-010 - set when this anomaly was grouped with others detected in the same short window; see getAnomalyCluster(). */
  cluster_id?: string | null;
}

export interface AnomalyClusterMember {
  id: string;
  type: string;
  service: string;
  provider: string;
  severity: string;
  current_cost: number;
  normal_cost: number;
  delta_amount: number | null;
  delta_percent: number | null;
  message: string;
  detected_at: string;
  status: string;
}

export interface AnomalyCluster {
  id: string;
  dominant_type: string;
  member_count: number;
  window_start: string;
  window_end: string;
  narrative: string;
  narrative_tier: string;
  created_at: string;
  members: AnomalyClusterMember[];
}

export interface PlatformAnomaly {
  id: string;
  provider: string;
  service_name: string;
  detected_date: string;
  tenant_count_affected: number;
  total_tenant_count: number;
  severity: string;
  avg_delta_percent: number | null;
  message: string;
  status: string;
  created_at: string;
}

export interface AnomalySummary {
  active_anomalies: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  total_impact: number;
  latest_anomaly?: Anomaly;
}

export interface AnomalyFilters {
  provider?: 'aws' | 'azure' | 'gcp';
  severity?: 'critical' | 'high' | 'medium' | 'low' | 'all';
  type?: string;
  status?: string;
  lookbackDays?: number;
  lookbackWeeks?: number;
  /** Bypasses the backend's default informational-noise filter (which hides
   * new_service findings and increases under 100%). See the API's
   * hidden_by_default_filter count. */
  showAll?: boolean;
}

/**
 * Get all anomalies with optional filters
 */
export const getAnomalies = async (
  filters?: AnomalyFilters
): Promise<{
  anomalies_count: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  anomalies: Anomaly[];
  /** How many real findings the default informational filter is holding back.
   * 0 when an explicit type filter or showAll is in play. */
  hidden_by_default_filter?: number;
}> => {
  const params = new URLSearchParams();

  if (filters?.provider) params.append('provider', filters.provider);
  if (filters?.severity) params.append('severity', filters.severity);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.showAll) params.append('showAll', 'true');
  if (filters?.lookbackDays)
    params.append('lookbackDays', filters.lookbackDays.toString());
  if (filters?.lookbackWeeks)
    params.append('lookbackWeeks', filters.lookbackWeeks.toString());

  const response = await axios.get(`${API_BASE_URL}/anomalies?${params.toString()}`, {
    headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
  });

  return response.data.data;
};

/**
 * Get anomaly summary statistics
 */
export const getAnomalySummary = async (
  provider?: 'aws' | 'azure' | 'gcp'
): Promise<AnomalySummary> => {
  const params = new URLSearchParams();
  if (provider) params.append('provider', provider);

  const response = await axios.get(
    `${API_BASE_URL}/anomalies/summary?${params.toString()}`,
    {
      headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
    }
  );

  return response.data.data;
};

/**
 * Get a specific anomaly by ID
 */
export const getAnomalyById = async (id: string): Promise<Anomaly> => {
  const response = await axios.get(`${API_BASE_URL}/anomalies/${id}`, {
    headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
  });

  return response.data.data;
};

/**
 * Acknowledge an anomaly
 */
export const acknowledgeAnomaly = async (
  id: string,
  acknowledgedBy: string,
  notes?: string
): Promise<Anomaly> => {
  const response = await axios.post(
    `${API_BASE_URL}/anomalies/${id}/acknowledge`,
    {
      acknowledged_by: acknowledgedBy,
      notes,
    },
    {
      headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
    }
  );

  return response.data.data;
};

/**
 * Resolve an anomaly
 */
export const resolveAnomaly = async (
  id: string,
  resolvedBy: string,
  resolution?: string
): Promise<Anomaly> => {
  const response = await axios.post(
    `${API_BASE_URL}/anomalies/${id}/resolve`,
    {
      resolved_by: resolvedBy,
      resolution,
    },
    {
      headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
    }
  );

  return response.data.data;
};

/**
 * Mark anomaly as false positive
 */
export const markAnomalyAsFalsePositive = async (
  id: string,
  markedBy: string,
  reason?: string
): Promise<Anomaly> => {
  const response = await axios.post(
    `${API_BASE_URL}/anomalies/${id}/false-positive`,
    {
      marked_by: markedBy,
      reason,
    },
    {
      headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
    }
  );

  return response.data.data;
};

/**
 * Get anomaly statistics
 */
export const getAnomalyStatistics = async (): Promise<{
  total: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  byType: Record<string, number>;
  byProvider: Record<string, number>;
}> => {
  const response = await axios.get(`${API_BASE_URL}/anomalies/stats`, {
    headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
  });

  return response.data.data;
};

/**
 * FR-ANOM-010 - the cluster narrative + member list for an anomaly that was
 * grouped with others detected in the same short window.
 */
export const getAnomalyCluster = async (clusterId: string): Promise<AnomalyCluster> => {
  const response = await axios.get(`${API_BASE_URL}/anomalies/clusters/${clusterId}`, {
    headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
  });
  return response.data.data;
};

/**
 * FR-ANOM-014 - platform-ops view, never tenant-scoped. Admin-only in
 * practice (the backend route lives under /admin), reused here since it
 * follows the same request shape as everything else in this file.
 */
export const getPlatformAnomalies = async (): Promise<PlatformAnomaly[]> => {
  const response = await axios.get(`${ADMIN_BASE_URL}/anomalies/platform-wide`, {
    headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
  });
  return response.data.data;
};

export const triggerPlatformAnomalyDetection = async (): Promise<void> => {
  await axios.post(`${ADMIN_BASE_URL}/anomalies/detect-platform-wide`, {}, {
    headers: authHeaders(), timeout: DEFAULT_TIMEOUT,
  });
};

/**
 * RCA (Root Cause Analysis) types
 */
export interface RCAAnalysis {
  summary: string;
  likely_causes: string[];
  investigation_steps: string[];
  prevention: string[];
  confidence: number;
  generated_at: string;
  model: string;
}

/**
 * Generate AI-powered Root Cause Analysis for an anomaly
 */
export const analyzeRCA = async (id: string): Promise<RCAAnalysis> => {
  const response = await axios.post(
    `${API_BASE_URL}/anomalies/${id}/analyze-rca`,
    {},
    {
      headers: authHeaders(), timeout: 60000, // 60 seconds for AI generation
    }
  );

  return response.data.data;
};
